/**
 * Ops Obligations HTTP client — list/create/patch/delete + attachments.
 */
import { opsFetch } from "@/lib/opsPaperworkClientBase";
import { requireSupabase } from "@/lib/supabase";
import type {
  OpsObligationCadence,
  OpsObligationDetails,
  OpsObligationKind,
  OpsObligationStatus,
  OpsUtilitySubtype,
} from "@/lib/opsObligations/opsObligationFields";

export type OpsObligation = {
  id: string;
  organization_id: string;
  location_id: string | null;
  kind: OpsObligationKind;
  utility_subtype: OpsUtilitySubtype | null;
  provider_name: string;
  account_ref: string;
  description: string;
  payment_url: string | null;
  cadence: OpsObligationCadence;
  due_rule: string;
  amount_cents: number | null;
  responsible_party: string;
  provider_contact: string;
  status: OpsObligationStatus;
  starts_on: string | null;
  ends_on: string | null;
  notes: string;
  details: OpsObligationDetails;
  created_at: string;
  updated_at: string;
};

export type OpsObligationLocationChoice = {
  id: string;
  name: string;
  is_active: boolean;
};

export type OpsObligationAttachment = {
  id: string;
  obligation_id: string;
  object_path: string;
  content_type: string;
  byte_size: number;
  display_name: string | null;
  sort_order: number;
  created_at: string;
  signed_url?: string;
  signed_expires_at?: string;
};

const BASE = "/api/ops/obligations";
const ATTACH_BASE = "/api/ops/obligation-attachments";

export type OpsObligationWrite = Partial<{
  kind: OpsObligationKind;
  utility_subtype: OpsUtilitySubtype | null;
  location_id: string | null;
  provider_name: string;
  account_ref: string;
  description: string;
  payment_url: string | null;
  cadence: OpsObligationCadence;
  due_rule: string;
  amount_cents: number | null;
  responsible_party: string;
  provider_contact: string;
  status: OpsObligationStatus;
  starts_on: string | null;
  ends_on: string | null;
  notes: string;
  details: OpsObligationDetails;
}>;

export function listOpsObligationsApi(filter?: {
  kind?: string;
  status?: string;
  location_id?: string;
}) {
  const p = new URLSearchParams();
  if (filter?.kind) p.set("kind", filter.kind);
  if (filter?.status) p.set("status", filter.status);
  if (filter?.location_id) p.set("location_id", filter.location_id);
  const q = p.toString();
  return opsFetch<{ ok: true; obligations: OpsObligation[]; locations: OpsObligationLocationChoice[] }>(
    q ? `${BASE}?${q}` : BASE,
    { method: "GET" },
  );
}

export function getOpsObligationApi(id: string) {
  const p = new URLSearchParams({ id, sign_media: "1" });
  return opsFetch<{ ok: true; obligation: OpsObligation; attachments: OpsObligationAttachment[] }>(
    `${BASE}?${p.toString()}`,
    { method: "GET" },
  );
}

export function createOpsObligationApi(body: OpsObligationWrite) {
  return opsFetch<{ ok: true; obligation: OpsObligation }>(BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function patchOpsObligationApi(id: string, body: OpsObligationWrite) {
  return opsFetch<{ ok: true; obligation: OpsObligation }>(`${BASE}?id=${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function deleteOpsObligationApi(id: string) {
  return opsFetch<{ ok: true; deleted: boolean; ended: boolean }>(
    `${BASE}?id=${encodeURIComponent(id)}`,
    { method: "DELETE" },
  );
}

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  attachment_id: string;
  content_type: string;
};

export async function uploadOpsObligationAttachment(
  obligationId: string,
  file: File,
): Promise<{ ok: true; attachment: OpsObligationAttachment } | { ok: false; error: string }> {
  const sb = requireSupabase();
  const ct = (file.type || "application/octet-stream").toLowerCase();
  const sign = await opsFetch<SignOk>(ATTACH_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "sign",
      obligation_id: obligationId,
      content_type: ct,
      byte_size: file.size,
      display_name: file.name,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  const s = sign.data;
  if (!s?.path || !s.token || !s.attachment_id) {
    return { ok: false, error: "Sign response invalid" };
  }
  const { error: upErr } = await sb.storage.from(s.bucket).uploadToSignedUrl(s.path, s.token, file, {
    contentType: s.content_type,
    upsert: false,
  });
  if (upErr) return { ok: false, error: upErr.message };
  const commit = await opsFetch<{ ok: true; attachment: OpsObligationAttachment }>(ATTACH_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      op: "commit",
      obligation_id: obligationId,
      attachment_id: s.attachment_id,
      object_path: s.path,
      content_type: s.content_type,
      byte_size: file.size,
      display_name: file.name,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  return { ok: true, attachment: commit.data.attachment };
}

export function deleteOpsObligationAttachment(attachmentId: string) {
  return opsFetch<{ ok: true; deleted_count: number }>(
    `${ATTACH_BASE}?id=${encodeURIComponent(attachmentId)}`,
    { method: "DELETE" },
  );
}
